import React, {useEffect, useState} from 'react';
import {App, Button, Checkbox, Input, InputNumber, Modal, Segmented, Select} from 'antd';
import {ExperimentOutlined} from '@ant-design/icons';
import {useI18n} from '../../../i18n/useI18n';
import {createDcaPlan, updateDcaPlan} from '../../../services/dcaService';
import type {DcaCondition, DcaPlan, DcaPlanDraft} from '../../../types/dca';
import {DCA_TIMEFRAMES, defaultCondition, parseConditionsJson, validateCondition,} from './dcaConditionUtil';
import DcaBacktestModal from './DcaBacktestModal';
import DcaConditionEditor from './DcaConditionEditor';

interface Props {
    open: boolean;
    /** null=创建；非 null=编辑该计划 */
    plan: DcaPlan | null;
    /** 可选品种（用户 DCA 配置 allowedSymbols） */
    symbols: string[];
    /** 创建模式预填草稿（首页计算器「按此配置创建计划」） */
    draft?: DcaPlanDraft | null;
    /** 平台硬上限：单计划买入次数（不传=不限制本地输入，后端仍强校验） */
    maxPlanBuyCount?: number | null;
    /** 平台硬上限：单计划单日触发次数 */
    maxPlanDailyTriggers?: number | null;
    onClose: () => void;
    onSaved: (plan: DcaPlan) => void;
}

/**
 * DCA 计划创建/编辑弹窗
 * 基础字段 + 条件组合器（类型/参数/AND-OR/≤5条/实时预览）+ 创建必勾风险确认；
 * 参数校验与后端 11.4 表一致（先本地拦截，后端强校验兜底）。
 */
const DcaPlanFormModal: React.FC<Props> = ({
                                               open,
                                               plan,
                                               symbols,
                                               draft,
                                               maxPlanBuyCount,
                                               maxPlanDailyTriggers,
                                               onClose,
                                               onSaved,
                                           }) => {
    const {t} = useI18n();
    const {message} = App.useApp();
    const isEdit = null != plan;

    const [planName, setPlanName] = useState('');
    const [symbol, setSymbol] = useState<string | undefined>();
    const [lifecycle, setLifecycle] = useState<'ONCE' | 'LOOP'>('ONCE');
    const [timeframe, setTimeframe] = useState('15m');
    const [monitorFrequency, setMonitorFrequency] = useState<number>(5);
    const [maxBuyCount, setMaxBuyCount] = useState<number>(10);
    const [dailyMaxTriggers, setDailyMaxTriggers] = useState<number | null>(null);
    const [op, setOp] = useState<'AND' | 'OR'>('AND');
    const [conditions, setConditions] = useState<DcaCondition[]>([defaultCondition('EMA_CROSS')]);
    const [riskConfirmed, setRiskConfirmed] = useState(false);
    const [saving, setSaving] = useState(false);
    /** 测算弹窗开关（P2：表单当前条件历史回放） */
    const [backtestOpen, setBacktestOpen] = useState(false);
    /** 打开测算时快照的条件组合 JSON（弹窗打开期间表单被遮挡不可改） */
    const [backtestJson, setBacktestJson] = useState('{}');

    // 打开时初始化：编辑回填计划值，创建重置默认值；关闭时同步关掉嵌套的测算弹窗
    useEffect(() => {
        if (!open) {
            setBacktestOpen(false);
            return;
        }
        if (null != plan) {
            setPlanName(plan.planName);
            setSymbol(plan.symbol);
            setLifecycle(plan.lifecycle);
            setTimeframe(plan.timeframe);
            setMonitorFrequency(plan.monitorFrequency);
            setMaxBuyCount(plan.maxBuyCount ?? 10);
            setDailyMaxTriggers(plan.dailyMaxTriggers ?? null);
            const parsed = parseConditionsJson(plan.conditionsJson);
            if (null != parsed) {
                setOp(parsed.op);
                setConditions(parsed.conditions);
            }
            setRiskConfirmed(false);
        } else if (null != draft) {
            // 计算器预填：仅覆盖草稿携带的字段，其余保持创建默认值
            setPlanName('');
            setSymbol(draft.symbol || undefined);
            setLifecycle(draft.lifecycle);
            setTimeframe(draft.timeframe);
            setMonitorFrequency(draft.monitorFrequency);
            setMaxBuyCount(draft.maxBuyCount);
            setDailyMaxTriggers(null);
            const parsed = parseConditionsJson(draft.conditionsJson);
            setOp(null == parsed ? 'AND' : parsed.op);
            setConditions(null == parsed ? [defaultCondition('EMA_CROSS')] : parsed.conditions);
            setRiskConfirmed(false);
        } else {
            setPlanName('');
            setSymbol(undefined);
            setLifecycle('ONCE');
            setTimeframe('15m');
            setMonitorFrequency(5);
            setMaxBuyCount(10);
            setDailyMaxTriggers(null);
            setOp('AND');
            setConditions([defaultCondition('EMA_CROSS')]);
            setRiskConfirmed(false);
        }
    }, [open, plan, draft]);

    /** 测算入口：本地校验品种/条件/LOOP上限后打开回测弹窗（P2） */
    const openBacktest = () => {
        if (null == symbol || '' === symbol) {
            message.warning(t('dca.form.symbolRequired'));
            return;
        }
        if (0 === conditions.length) {
            message.warning(t('dca.form.conditionEmpty'));
            return;
        }
        for (const cond of conditions) {
            const err = validateCondition(cond, t);
            if (null != err) {
                message.warning(err);
                return;
            }
        }
        if ('LOOP' === lifecycle && (null == maxBuyCount || maxBuyCount < 1)) {
            message.warning(t('dca.form.maxBuyCountRequired'));
            return;
        }
        setBacktestJson(JSON.stringify({op, conditions}));
        setBacktestOpen(true);
    };

    const handleSubmit = async () => {
        const name = planName.trim();
        if ('' === name) {
            message.warning(t('dca.form.planNameRequired'));
            return;
        }
        if (name.length > 64) {
            message.warning(t('dca.form.planNameMax'));
            return;
        }
        if (null == symbol || '' === symbol) {
            message.warning(t('dca.form.symbolRequired'));
            return;
        }
        if (null == monitorFrequency || monitorFrequency < 1 || monitorFrequency > 1440) {
            message.warning(t('dca.form.monitorFrequencyRequired'));
            return;
        }
        if ('LOOP' === lifecycle && (null == maxBuyCount || maxBuyCount < 1)) {
            message.warning(t('dca.form.maxBuyCountRequired'));
            return;
        }
        if (null != dailyMaxTriggers && dailyMaxTriggers < 1) {
            message.warning(t('dca.form.paramInvalid'));
            return;
        }
        if (0 === conditions.length) {
            message.warning(t('dca.form.conditionEmpty'));
            return;
        }
        for (const cond of conditions) {
            const err = validateCondition(cond, t);
            if (null != err) {
                message.warning(err);
                return;
            }
        }
        if (!isEdit && !riskConfirmed) {
            message.warning(t('dca.form.riskRequired'));
            return;
        }

        const conditionsJson = JSON.stringify({op, conditions});
        // 编辑模式下清空单日上限=保持不变（后端更新接口 null 表示不修改）
        const dailyCap = null == dailyMaxTriggers && isEdit ? plan?.dailyMaxTriggers ?? null : dailyMaxTriggers;
        setSaving(true);
        try {
            if (null != plan) {
                const updated = await updateDcaPlan(plan.id, {
                    planName: name,
                    symbol,
                    lifecycle,
                    timeframe,
                    monitorFrequency,
                    maxBuyCount: 'LOOP' === lifecycle ? maxBuyCount : 1,
                    dailyMaxTriggers: dailyCap,
                    conditionsJson,
                });
                message.success(t('dca.form.saveOk'));
                if (null != updated) {
                    onSaved(updated);
                }
            } else {
                const created = await createDcaPlan({
                    planName: name,
                    symbol,
                    lifecycle,
                    timeframe,
                    monitorFrequency,
                    maxBuyCount: 'LOOP' === lifecycle ? maxBuyCount : 1,
                    dailyMaxTriggers,
                    conditionsJson,
                    riskConfirmed,
                });
                message.success(t('dca.form.saveOk'));
                if (null != created) {
                    onSaved(created);
                }
            }
            onClose();
        } catch (e: any) {
            message.error(e?.message ?? t('dca.form.saveFail'));
        } finally {
            setSaving(false);
        }
    };

    return (
        <>
            <Modal
                open={open}
                title={isEdit ? t('dca.form.title.edit') : t('dca.form.title.create')}
                width={760}
                onCancel={onClose}
                maskClosable={false}
                footer={[
                    <Button key="cancel" onClick={onClose}>{t('dca.common.cancel')}</Button>,
                    <Button key="backtest" icon={<ExperimentOutlined/>} onClick={openBacktest}>
                        {t('dca.form.backtest')}
                    </Button>,
                    <Button key="submit" type="primary" loading={saving} onClick={() => void handleSubmit()}>
                        {isEdit ? t('dca.form.save') : t('dca.form.submit')}
                    </Button>,
                ]}
            >
                <div className="dca-form">
                    <div className="dca-form-row">
                        <div className="dca-form-item dca-form-grow">
                            <div className="dca-form-label">{t('dca.form.planName')}</div>
                            <Input maxLength={64} value={planName} placeholder={t('dca.form.planNamePlaceholder')}
                                   onChange={(e) => setPlanName(e.target.value)}/>
                        </div>
                        <div className="dca-form-item">
                            <div className="dca-form-label">{t('dca.form.symbol')}</div>
                            <Select style={{width: 150}} value={symbol} placeholder={t('dca.form.symbolEmpty')}
                                    options={symbols.map((s) => ({label: s, value: s}))}
                                    onChange={(v) => setSymbol(v)}/>
                        </div>
                    </div>
                    <div className="dca-form-row">
                        <div className="dca-form-item">
                            <div className="dca-form-label">{t('dca.form.lifecycle')}</div>
                            <Segmented
                                value={lifecycle}
                                options={[
                                    {label: t('dca.plan.lifecycle.ONCE'), value: 'ONCE'},
                                    {label: t('dca.plan.lifecycle.LOOP'), value: 'LOOP'},
                                ]}
                                onChange={(v) => setLifecycle(v as 'ONCE' | 'LOOP')}
                            />
                        </div>
                        <div className="dca-form-item">
                            <div className="dca-form-label">{t('dca.form.timeframe')}</div>
                            <Select style={{width: 110}} value={timeframe}
                                    options={DCA_TIMEFRAMES.map((tf) => ({label: tf, value: tf}))}
                                    onChange={(v) => setTimeframe(v)}/>
                        </div>
                        <div className="dca-form-item">
                            <div className="dca-form-label">{t('dca.form.monitorFrequency')}</div>
                            <InputNumber min={1} max={1440} value={monitorFrequency} style={{width: 130}}
                                         onChange={(v) => setMonitorFrequency(v ?? 1)}/>
                        </div>
                    </div>
                    <div className="dca-form-row">
                        {'LOOP' === lifecycle ? (
                            <div className="dca-form-item">
                                <div className="dca-form-label">{t('dca.form.maxBuyCount')}</div>
                                <InputNumber min={1} max={maxPlanBuyCount ?? undefined} precision={0}
                                             value={maxBuyCount} style={{width: 130}}
                                             onChange={(v) => setMaxBuyCount(v ?? 1)}/>
                            </div>
                        ) : null}
                        <div className="dca-form-item">
                            <div className="dca-form-label">{t('dca.form.dailyMaxTriggers')}</div>
                            <InputNumber min={1} max={maxPlanDailyTriggers ?? undefined} precision={0}
                                         value={dailyMaxTriggers} style={{width: 130}}
                                         placeholder={isEdit
                                             ? t('dca.form.dailyMaxTriggersPlaceholderEdit')
                                             : t('dca.form.dailyMaxTriggersPlaceholder')}
                                         onChange={(v) => setDailyMaxTriggers(v ?? null)}/>
                        </div>
                    </div>

                    <div className="dca-form-label">{t('dca.form.conditions')}</div>
                    <div className="dca-form-tip">{t('dca.form.conditionsTip')}</div>
                    <DcaConditionEditor
                        conditions={conditions}
                        op={op}
                        onChange={(nextOp, nextConditions) => {
                            setOp(nextOp);
                            setConditions(nextConditions);
                        }}
                    />

                    {!isEdit ? (
                        <Checkbox checked={riskConfirmed} onChange={(e) => setRiskConfirmed(e.target.checked)}
                                  className="dca-risk">
                            {t('dca.form.riskText')}
                        </Checkbox>
                    ) : null}
                </div>
            </Modal>
            <DcaBacktestModal
                open={backtestOpen}
                conditionsJson={backtestJson}
                symbol={symbol ?? ''}
                timeframe={timeframe}
                lifecycle={lifecycle}
                maxBuyCount={'LOOP' === lifecycle ? (maxBuyCount ?? 1) : 1}
                onClose={() => setBacktestOpen(false)}
            />
        </>
    );
};

export default DcaPlanFormModal;
